import * as echarts from 'echarts';
import {useEffect, useMemo, useRef, useState} from 'react';
import {AbsoluteFill} from 'remotion';
import {dataset, type WeeklyDatum} from './data';
import {fontFamily, theme} from './theme';

type RangeKey = 'all' | '2y' | '1y';
type MetricKey = 'both' | 'sessions' | 'minutes';

const numberFormatter = new Intl.NumberFormat('zh-CN');

const rangeOptions: Array<{key: RangeKey; label: string; weeks: number | null}> = [
  {key: 'all', label: '全部', weeks: null},
  {key: '2y', label: '近两年', weeks: 104},
  {key: '1y', label: '近一年', weeks: 52},
];

const metricOptions: Array<{key: MetricKey; label: string}> = [
  {key: 'both', label: '双指标'},
  {key: 'sessions', label: '会话次数'},
  {key: 'minutes', label: '工作分钟'},
];

const ControlGroup = <T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{key: T; label: string}>;
  value: T;
  onChange: (value: T) => void;
}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
    <span style={{color: theme.muted, fontSize: 15, letterSpacing: 0.5}}>{label}</span>
    <div
      style={{
        display: 'flex',
        gap: 4,
        padding: 4,
        border: `1px solid ${theme.line}`,
        borderRadius: 10,
        background: 'rgba(4, 16, 29, 0.72)',
      }}
    >
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.key)}
            style={{
              border: 0,
              borderRadius: 7,
              padding: '9px 16px',
              background: active ? 'rgba(33, 212, 253, 0.16)' : 'transparent',
              color: active ? theme.text : theme.muted,
              boxShadow: active ? 'inset 0 0 0 1px rgba(33, 212, 253, 0.36)' : 'none',
              fontFamily,
              fontSize: 15,
              fontWeight: active ? 600 : 450,
              cursor: 'pointer',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

const Stat = ({label, value, suffix, color}: {label: string; value: string; suffix: string; color: string}) => (
  <div
    style={{
      flex: 1,
      minWidth: 0,
      padding: '18px 22px',
      borderLeft: `2px solid ${color}`,
      background: 'linear-gradient(90deg, rgba(16, 39, 58, 0.76), rgba(8, 24, 39, 0.42))',
    }}
  >
    <div style={{color: theme.muted, fontSize: 14, letterSpacing: 1}}>{label}</div>
    <div style={{marginTop: 9, color: theme.text, fontSize: 28, fontWeight: 650, letterSpacing: 0.3}}>
      {value}
      <span style={{marginLeft: 7, color: theme.muted, fontSize: 14, fontWeight: 450}}>{suffix}</span>
    </div>
  </div>
);

export const InteractiveDashboard = () => {
  const chartElementRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const [range, setRange] = useState<RangeKey>('all');
  const [metric, setMetric] = useState<MetricKey>('both');
  const [selectedRow, setSelectedRow] = useState<WeeklyDatum>(dataset.rows.at(-1) ?? dataset.rows[0]);

  const visibleRows = useMemo(() => {
    const weeks = rangeOptions.find((option) => option.key === range)?.weeks;
    return weeks ? dataset.rows.slice(-weeks) : dataset.rows;
  }, [range]);

  const visibleSummary = useMemo(() => {
    const totalSessions = visibleRows.reduce((total, row) => total + row.sessions, 0);
    const totalMinutes = visibleRows.reduce((total, row) => total + row.minutes, 0);
    return {
      totalSessions,
      totalMinutes,
      averageSessions: Math.round(totalSessions / visibleRows.length),
      averageMinutes: Math.round(totalMinutes / visibleRows.length),
    };
  }, [visibleRows]);

  useEffect(() => {
    const element = chartElementRef.current;
    if (!element) return;

    const chart = echarts.init(element, undefined, {renderer: 'canvas'});
    chartRef.current = chart;
    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const showSessions = metric !== 'minutes';
    const showMinutes = metric !== 'sessions';
    chart.setOption(
      {
        animation: false,
        color: [theme.sessions, theme.minutes],
        backgroundColor: 'transparent',
        grid: {left: 76, right: 78, top: 72, bottom: 100},
        legend: {
          top: 16,
          left: 74,
          itemWidth: 28,
          itemHeight: 4,
          itemGap: 30,
          textStyle: {color: '#C8D6E3', fontFamily, fontSize: 15},
          selected: {工作会话次数: showSessions, 工作分钟: showMinutes},
          data: ['工作会话次数', '工作分钟'],
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(3, 12, 23, 0.96)',
          borderColor: theme.line,
          borderWidth: 1,
          padding: [14, 16],
          textStyle: {color: theme.text, fontFamily, fontSize: 14},
          axisPointer: {type: 'cross', lineStyle: {color: 'rgba(150, 183, 211, 0.38)'}},
          valueFormatter: (value: unknown) => numberFormatter.format(Number(value)),
        },
        toolbox: {
          top: 10,
          right: 66,
          iconStyle: {borderColor: theme.muted},
          emphasis: {iconStyle: {borderColor: theme.sessions}},
          feature: {dataZoom: {yAxisIndex: 'none'}, restore: {}, saveAsImage: {name: 'weekly-work-trend'}},
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: visibleRows.map((row) => row.startDate),
          axisLine: {lineStyle: {color: theme.line}},
          axisTick: {show: false},
          axisLabel: {color: theme.muted, fontFamily, fontSize: 13, margin: 18, hideOverlap: true},
        },
        yAxis: [
          {
            type: 'value',
            name: '会话 / 次',
            nameTextStyle: {color: theme.sessions, fontFamily, padding: [0, 0, 10, -20]},
            axisLabel: {color: theme.muted, fontFamily},
            splitLine: {lineStyle: {color: theme.grid, type: 'dashed'}},
          },
          {
            type: 'value',
            name: '时间 / 分钟',
            nameTextStyle: {color: theme.minutes, fontFamily, padding: [0, -14, 10, 0]},
            axisLabel: {color: theme.muted, fontFamily},
            splitLine: {show: false},
          },
        ],
        dataZoom: [
          {type: 'inside', start: 0, end: 100, zoomOnMouseWheel: true, moveOnMouseMove: true},
          {
            type: 'slider',
            height: 24,
            bottom: 32,
            borderColor: theme.line,
            backgroundColor: 'rgba(4, 16, 29, 0.78)',
            fillerColor: 'rgba(33, 212, 253, 0.14)',
            handleStyle: {color: theme.sessions, borderColor: theme.sessions},
            moveHandleStyle: {color: theme.sessions},
            textStyle: {color: theme.muted, fontFamily},
            dataBackground: {lineStyle: {color: '#38526A'}, areaStyle: {color: '#173149'}},
            selectedDataBackground: {lineStyle: {color: theme.sessions}, areaStyle: {color: '#0C5D70'}},
          },
        ],
        series: [
          {
            name: '工作会话次数',
            type: 'line',
            yAxisIndex: 0,
            data: visibleRows.map((row) => row.sessions),
            smooth: 0.28,
            symbol: 'circle',
            showSymbol: false,
            symbolSize: 9,
            lineStyle: {width: 3, shadowBlur: 14, shadowColor: 'rgba(33, 212, 253, 0.28)'},
            itemStyle: {color: theme.sessions, borderColor: theme.background, borderWidth: 3},
            emphasis: {focus: 'series', scale: 1.4},
            areaStyle: {
              opacity: 0.16,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {offset: 0, color: theme.sessions},
                {offset: 1, color: 'rgba(33, 212, 253, 0)'},
              ]),
            },
          },
          {
            name: '工作分钟',
            type: 'line',
            yAxisIndex: 1,
            data: visibleRows.map((row) => row.minutes),
            smooth: 0.28,
            symbol: 'circle',
            showSymbol: false,
            symbolSize: 9,
            lineStyle: {width: 3, shadowBlur: 14, shadowColor: 'rgba(255, 170, 51, 0.24)'},
            itemStyle: {color: theme.minutes, borderColor: theme.background, borderWidth: 3},
            emphasis: {focus: 'series', scale: 1.4},
          },
        ],
      },
      {notMerge: true},
    );

    const handleClick = (params: {dataIndex?: number}) => {
      if (typeof params.dataIndex === 'number') {
        setSelectedRow(visibleRows[params.dataIndex]);
      }
    };
    chart.on('click', handleClick);
    return () => {
      chart.off('click', handleClick);
    };
  }, [metric, visibleRows]);

  useEffect(() => {
    if (!visibleRows.includes(selectedRow)) {
      setSelectedRow(visibleRows.at(-1) ?? visibleRows[0]);
    }
  }, [selectedRow, visibleRows]);

  return (
    <AbsoluteFill
      style={{
        overflow: 'hidden',
        pointerEvents: 'auto',
        background: `radial-gradient(circle at 48% 4%, #0B2941 0%, ${theme.background} 38%, ${theme.backgroundDeep} 100%)`,
        color: theme.text,
        fontFamily,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.3,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(43, 88, 122, 0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(43, 88, 122, 0.14) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'linear-gradient(to bottom, black, transparent 92%)',
        }}
      />

      <main style={{position: 'relative', zIndex: 1, height: '100%', padding: '58px 72px 48px', boxSizing: 'border-box'}}>
        <header style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
          <div>
            <h1 style={{margin: 0, fontSize: 42, lineHeight: 1.15, fontWeight: 680, letterSpacing: 1.5}}>
              工作数据交互看板
            </h1>
            <div style={{marginTop: 13, color: theme.muted, fontSize: 16, letterSpacing: 1.1}}>
              {dataset.summary.startDate.replaceAll('-', '.')} — {dataset.summary.endDate.replaceAll('-', '.')} · {dataset.summary.count} 周
            </div>
          </div>
          <div style={{display: 'flex', gap: 28, paddingTop: 4}}>
            <ControlGroup label="范围" options={rangeOptions} value={range} onChange={setRange} />
            <ControlGroup label="指标" options={metricOptions} value={metric} onChange={setMetric} />
          </div>
        </header>

        <section style={{display: 'flex', gap: 12, marginTop: 34}}>
          <Stat label="总会话次数" value={numberFormatter.format(visibleSummary.totalSessions)} suffix="次" color={theme.sessions} />
          <Stat label="平均每周会话" value={numberFormatter.format(visibleSummary.averageSessions)} suffix="次" color={theme.sessions} />
          <Stat label="总工作时间" value={numberFormatter.format(visibleSummary.totalMinutes)} suffix="分钟" color={theme.minutes} />
          <Stat label="平均每周时间" value={numberFormatter.format(visibleSummary.averageMinutes)} suffix="分钟" color={theme.minutes} />
        </section>

        <section
          style={{
            position: 'relative',
            height: 590,
            marginTop: 20,
            border: `1px solid ${theme.line}`,
            borderRadius: 18,
            background: 'linear-gradient(180deg, rgba(8, 27, 44, 0.88), rgba(3, 13, 24, 0.72))',
            boxShadow: '0 30px 80px rgba(0, 0, 0, 0.24)',
          }}
        >
          <div ref={chartElementRef} style={{width: '100%', height: '100%'}} />
        </section>

        <footer style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20}}>
          <div style={{display: 'flex', alignItems: 'baseline', gap: 20}}>
            <span style={{color: theme.muted, fontSize: 14, letterSpacing: 1}}>当前选择</span>
            <strong style={{fontSize: 18, fontWeight: 600}}>{selectedRow.dateLabel}</strong>
            <span style={{color: theme.sessions, fontSize: 18, fontWeight: 650}}>
              {numberFormatter.format(selectedRow.sessions)} 次会话
            </span>
            <span style={{color: theme.minutes, fontSize: 18, fontWeight: 650}}>
              {numberFormatter.format(selectedRow.minutes)} 分钟
            </span>
          </div>
          <div style={{color: theme.muted, fontSize: 14, letterSpacing: 0.5}}>
            悬浮查看数值 · 点击数据点查看明细 · 滚轮或拖动底部滑块缩放
          </div>
        </footer>
      </main>
    </AbsoluteFill>
  );
};
